
import React, {useEffect, useState} from 'react';
import {Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import apiService from '@/service/api-service';
import {store} from '@/service/store';
import {toast} from '@/hooks/use-toast';
import WarningAlert from '@/components/common/WarningAlert';

interface DeviceStepProps {
  deviceData: {
    taximeterVendor: string;
    taximeterType: string;
    taximeterSerial: string;
    taximeterYearOfProduction: string;
    deviceVendor: string;
    deviceType: string;
    deviceSerial: string;
    deviceYearOfProduction: string;
  };
  setDeviceData: (data: any) => void;
  onNextStep: () => void;
}

const DeviceStep: React.FC<DeviceStepProps> = ({ deviceData, setDeviceData, onNextStep }) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<{
    taximeterVendor?: boolean;
    taximeterType?: boolean;
    taximeterSerial?: boolean;
    taximeterYearOfProduction?: boolean;
    deviceVendor?: boolean;
    deviceType?: boolean;
    deviceSerial?: boolean;
    deviceYearOfProduction?: boolean;
  }>({});
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Determine if signing device is needed based on taximeter vendor and type
  const needsSigningDevice = () => {
    if (!deviceData.taximeterVendor) return false;

    // Semitron taximeters don't need signing device
    if (deviceData.taximeterVendor === 'semitron') return false;

    // Hale: only show signing device section if type is selected AND it's NOT MCT07 or WSZ07
    if (deviceData.taximeterVendor === 'hale') {
      if (!deviceData.taximeterType) return false;
      if (deviceData.taximeterType === 'MCT07' || deviceData.taximeterType === 'WSZ07') {
        return false;
      }
      return true;
    }

    // Digitax always needs signing device (when type is selected)
    if (deviceData.taximeterVendor === 'digitax' && deviceData.taximeterType) return true;

    return false;
  };

  const handleChange = (name: string, value: any) => {
    // Clear errors when field is changed
    setErrors(prev => ({ ...prev, [name]: false }));
    setDeviceError(null);

    let updatedData = {
      ...deviceData,
      [name]: value
    };

    // Reset taximeter type when vendor changes
    if (name === 'taximeterVendor') {
      updatedData.taximeterType = '';
      updatedData.taximeterYearOfProduction = '';
      updatedData.deviceVendor = '';
      updatedData.deviceType = '';
      updatedData.deviceSerial = '';
      updatedData.deviceYearOfProduction = '';
    }

    // Auto-fill signing device info based on taximeter selection
    if (name === 'taximeterType' || name === 'taximeterVendor') {
      const vendor = name === 'taximeterVendor' ? value : deviceData.taximeterVendor;
      const type = name === 'taximeterType' ? value : deviceData.taximeterType;

      if (vendor === 'hale') {
        if (type === 'MCT07' || type === 'WSZ07') {
          // These don't need separate signing device, but set deviceType to 9
          // All other values are synced from taximeter
          updatedData.deviceType = '9';
          updatedData.deviceVendor = 'hale';
          // Sync serial and year if already entered
          if (deviceData.taximeterSerial) {
            updatedData.deviceSerial = deviceData.taximeterSerial;
          }
          if (deviceData.taximeterYearOfProduction) {
            updatedData.deviceYearOfProduction = deviceData.taximeterYearOfProduction;
          }
        } else if (type) {
          // Other Hale taximeters need SEI03 Box
          updatedData.deviceType = '4';
          updatedData.deviceVendor = 'hale';
        }
      } else if (vendor === 'digitax' && type) {
        // Digitax always uses TaxiFusion TBOX with current year
        updatedData.deviceType = 'TBOX';
        updatedData.deviceVendor = 'taxifusion';
        updatedData.deviceYearOfProduction = new Date().getFullYear().toString();
      } else if (vendor === 'semitron') {
        // Semitron: taximeter and device are identical
        updatedData.deviceVendor = 'semitron';
        updatedData.deviceType = type || '';
        updatedData.deviceSerial = deviceData.taximeterSerial || '';
        updatedData.deviceYearOfProduction = deviceData.taximeterYearOfProduction || '';
      }
    }

    // Auto-fill year of production from serial number for Semitron devices
    if (name === 'taximeterSerial' && deviceData.taximeterVendor === 'semitron' && value.length >= 2) {
      const yearPrefix = value.substring(0, 2);
      const year = parseInt(yearPrefix);
      if (!isNaN(year)) {
        updatedData.taximeterYearOfProduction = `20${yearPrefix}`;
        updatedData.deviceYearOfProduction = `20${yearPrefix}`;
      }
    }

    // Auto-fill year of production from serial number for Digitax taximeters
    if (name === 'taximeterSerial' && deviceData.taximeterVendor === 'digitax' && value.length >= 2) {
      const yearPrefix = value.substring(0, 2);
      const year = parseInt(yearPrefix);
      if (!isNaN(year)) {
        updatedData.taximeterYearOfProduction = `20${yearPrefix}`;
      }
    }

    // For Semitron: sync taximeter values to device values
    if (deviceData.taximeterVendor === 'semitron') {
      if (name === 'taximeterSerial') {
        updatedData.deviceSerial = value;
      }
      if (name === 'taximeterYearOfProduction') {
        updatedData.deviceYearOfProduction = value;
      }
      if (name === 'taximeterType') {
        updatedData.deviceType = value;
      }
    }

    // For Hale MCT07/WSZ07: sync taximeter serial and year to device
    if (deviceData.taximeterVendor === 'hale' &&
        (deviceData.taximeterType === 'MCT07' || deviceData.taximeterType === 'WSZ07')) {
      if (name === 'taximeterSerial') {
        updatedData.deviceSerial = value;
      }
      if (name === 'taximeterYearOfProduction') {
        updatedData.deviceYearOfProduction = value;
      }
    }

    setDeviceData(updatedData);
  };

  const isFormValid = () => {
    // Always need taximeter info
    if (!deviceData.taximeterVendor || !deviceData.taximeterType ||
        !deviceData.taximeterSerial || !deviceData.taximeterYearOfProduction) {
      return false;
    }

    // If signing device is needed, validate those fields too
    if (needsSigningDevice()) {
      // For Digitax, deviceYearOfProduction is auto-filled, so don't validate it
      if (deviceData.taximeterVendor === 'digitax') {
        return !!(deviceData.deviceVendor && deviceData.deviceType && deviceData.deviceSerial);
      }
      return !!(deviceData.deviceVendor && deviceData.deviceType &&
                deviceData.deviceSerial && deviceData.deviceYearOfProduction);
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsLoading(true);
    try {
      await apiService.createDevice(deviceData);
      onNextStep();
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Mark all fields as error when device is not found
        setErrors({
          taximeterVendor: true,
          taximeterType: true,
          taximeterSerial: true,
          taximeterYearOfProduction: true,
          deviceVendor: true,
          deviceType: true,
          deviceSerial: true,
          deviceYearOfProduction: true
        });

        setDeviceError(t('assistant.deviceStep.notFound'));
      } else if (error.response?.status === 409) {
        toast({
          variant: "destructive",
          title: t('common.error'),
          description: t('assistant.deviceStep.alreadyInUse'),
        });
      } else {
        toast({
          variant: "destructive",
          title: t('common.error'),
          description: t('common.unexpectedError'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Register the submit function with the parent component
  useEffect(() => {
    // @ts-ignore - Custom property added to window
    if (window.registerDeviceStepSubmit) {
      // @ts-ignore - Custom property added to window
      window.registerDeviceStepSubmit(handleSubmit);
    }
  }, [deviceData]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'medium' }}>
        {t('assistant.deviceStep.title')}
      </Typography>

      {deviceError && (
        <WarningAlert
          message={deviceError}
          className="mb-4"
        />
      )}

      <Grid container spacing={3}>
        {/* Taximeter Vendor */}
        <Grid size={12}>
          <FormControl fullWidth error={errors.taximeterVendor} disabled={isLoading}>
            <InputLabel required={false}>{t('assistant.deviceStep.taximeterVendor')}</InputLabel>
            <Select
              label={t('assistant.deviceStep.taximeterVendor')}
              value={deviceData.taximeterVendor}
              onChange={(e) => handleChange('taximeterVendor', e.target.value)}
            >
              <MenuItem value="hale">Hale</MenuItem>
              <MenuItem value="semitron">Semitron</MenuItem>
              <MenuItem value="digitax">Digitax</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Taximeter Type */}
        <Grid size={12}>
          <FormControl fullWidth error={errors.taximeterType} disabled={isLoading || !deviceData.taximeterVendor}>
            <InputLabel required={false}>{t('assistant.deviceStep.taximeterType')}</InputLabel>
            <Select
              label={t('assistant.deviceStep.taximeterType')}
              value={deviceData.taximeterType}
              onChange={(e) => handleChange('taximeterType', e.target.value)}
              key={deviceData.taximeterVendor}
            >
              {deviceData.taximeterVendor === 'hale' && [
                <MenuItem key="MCT07" value="MCT07">MCT07</MenuItem>,
                <MenuItem key="MCT06" value="MCT06">MCT06</MenuItem>,
                <MenuItem key="SPT02" value="SPT02">SPT02</MenuItem>,
                <MenuItem key="SPT03" value="SPT03">SPT03</MenuItem>,
                <MenuItem key="SPT03R" value="SPT03R">SPT03R</MenuItem>,
                <MenuItem key="WSZ07" value="WSZ07">WSZ07</MenuItem>,
                <MenuItem key="WSZ06" value="WSZ06">WSZ06</MenuItem>,
                <MenuItem key="SPW02" value="SPW02">SPW02</MenuItem>,
                <MenuItem key="SPW03" value="SPW03">SPW03</MenuItem>,
                <MenuItem key="SPW03R" value="SPW03R">SPW03R</MenuItem>,
              ]}
              {deviceData.taximeterVendor === 'semitron' && [
                <MenuItem key="P6L" value="P6L">P6L</MenuItem>,
                <MenuItem key="P6L2" value="P6L2">P6L2</MenuItem>,
                <MenuItem key="P6S" value="P6S">P6S</MenuItem>,
                <MenuItem key="P6S2" value="P6S2">P6S2</MenuItem>,
                <MenuItem key="P6SW" value="P6SW">P6SW</MenuItem>,
                <MenuItem key="P6LW" value="P6LW">P6LW</MenuItem>
              ]}
              {deviceData.taximeterVendor === 'digitax' && [
                <MenuItem key="F1Plus" value="F1Plus">F1Plus</MenuItem>,
                <MenuItem key="M1Plus" value="M1Plus">M1Plus</MenuItem>,
                <MenuItem key="F2Plus" value="F2Plus">F2Plus</MenuItem>,
                <MenuItem key="F4Plus" value="F4Plus">F4Plus</MenuItem>
              ]}
            </Select>
          </FormControl>
        </Grid>

        {/* Taximeter Serial */}
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('assistant.deviceStep.taximeterSerial')}
            value={deviceData.taximeterSerial}
            onChange={(e) => handleChange('taximeterSerial', e.target.value)}
            variant="outlined"
            error={errors.taximeterSerial || (deviceData.taximeterSerial && deviceData.taximeterSerial.length < 4)}
            helperText={deviceData.taximeterSerial && deviceData.taximeterSerial.length < 4 ? t('assistant.deviceStep.taximeterSerialMinLength') : ''}
            slotProps={{
              inputLabel: { required: false }
            }}
            disabled={isLoading}
          />
        </Grid>

        {/* Taximeter Year of Production - Disabled for Semitron and Digitax (auto-filled from serial) */}
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('assistant.deviceStep.taximeterYearOfProduction')}
            value={deviceData.taximeterYearOfProduction}
            onChange={(e) => handleChange('taximeterYearOfProduction', e.target.value)}
            variant="outlined"
            type="number"
            error={errors.taximeterYearOfProduction}
            slotProps={{
              inputLabel: { required: false },
              input: { inputProps: { min: 1900, max: new Date().getFullYear() } }
            }}
            disabled={isLoading || deviceData.taximeterVendor === 'semitron' || deviceData.taximeterVendor === 'digitax'}
          />
        </Grid>

        {/* Signing Device Fields - Only show if needed */}
        {needsSigningDevice() && (
          <>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'medium' }}>
                Signiereinheit
              </Typography>
            </Grid>

            {/* Device Vendor - Disabled (auto-filled) */}
            <Grid size={12}>
              <FormControl fullWidth error={errors.deviceVendor} disabled={true}>
                <InputLabel required={false}>{t('assistant.deviceStep.deviceVendor')}</InputLabel>
                <Select
                  label={t('assistant.deviceStep.deviceVendor')}
                  value={deviceData.deviceVendor}
                  onChange={(e) => handleChange('deviceVendor', e.target.value)}
                >
                  <MenuItem value="hale">Hale</MenuItem>
                  <MenuItem value="taxifusion">TaxiFusion</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Device Type - Disabled (auto-filled) */}
            <Grid size={12}>
              <FormControl fullWidth error={errors.deviceType} disabled={true}>
                <InputLabel required={false}>{t('assistant.deviceStep.deviceType')}</InputLabel>
                <Select
                  label={t('assistant.deviceStep.deviceType')}
                  value={deviceData.deviceType}
                  onChange={(e) => handleChange('deviceType', e.target.value)}
                >
                  <MenuItem value="4">SEI-03 Box</MenuItem>
                  <MenuItem value="9">MCT07</MenuItem>
                  <MenuItem value="TBOX">TBOX</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Device Serial */}
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('assistant.deviceStep.deviceSerial')}
                value={deviceData.deviceSerial}
                onChange={(e) => handleChange('deviceSerial', e.target.value)}
                variant="outlined"
                error={errors.deviceSerial || (deviceData.deviceType === 'TBOX' && deviceData.deviceSerial && deviceData.deviceSerial.length !== 10)}
                helperText={deviceData.deviceType === 'TBOX' && deviceData.deviceSerial && deviceData.deviceSerial.length !== 10 ? t('assistant.deviceStep.tboxSerialLength') : ''}
                slotProps={{
                  inputLabel: { required: false }
                }}
                disabled={isLoading}
              />
            </Grid>

            {/* Device Year of Production - Only show for non-Digitax devices */}
            {deviceData.taximeterVendor !== 'digitax' && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('assistant.deviceStep.deviceYearOfProduction')}
                  value={deviceData.deviceYearOfProduction}
                  onChange={(e) => handleChange('deviceYearOfProduction', e.target.value)}
                  variant="outlined"
                  type="number"
                  error={errors.deviceYearOfProduction}
                  slotProps={{
                    inputLabel: { required: false },
                    input: { inputProps: { min: 1900, max: new Date().getFullYear() } }
                  }}
                  disabled={isLoading}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

export default DeviceStep;
